import { useState } from 'react'
import { format } from 'date-fns'
import { Calendar as CalendarIcon } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Calendar } from '@/components/ui/calendar'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { Input } from '@/components/ui/input'
interface DateInputProps {
  value: string
  onChange: (date: string) => void
  placeholder?: string
  className?: string
  id?: string
  label?: string
  required?: boolean
  disabled?: boolean
  /** Earliest selectable date, as YYYY-MM-DD. Dates before it are disabled. */
  minDate?: string
}

export function DateInput({
  value,
  onChange,
  placeholder = 'Select date',
  className,
  id,
  label,
  required = false,
  disabled = false,
  minDate
}: DateInputProps) {
  const [open, setOpen] = useState(false)

  const handleDateSelect = (date: Date | undefined) => {
    if (date) {
      // Convert to YYYY-MM-DD format for consistency
      const formattedDate = format(date, 'yyyy-MM-dd')
      onChange(formattedDate)
      setOpen(false)
    }
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Allow user to type directly
    const inputValue = e.target.value
    onChange(inputValue)
  }

  const displayValue = value ? format(new Date(value + 'T00:00:00'), 'dd/MM/yyyy') : ''

  const minDateObj = minDate ? new Date(minDate + 'T00:00:00') : new Date('1900-01-01')

  return (
    <div className={cn("flex flex-col space-y-2", className)}>
      {label && (
        <label
          htmlFor={id}
          className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
        >
          {label}
          {required && <span className="ml-0.5 text-rose-500" aria-hidden="true">*</span>}
        </label>
      )}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            className={cn(
              "w-full justify-start text-left font-normal border-input bg-background",
              !value && "text-muted-foreground",
              disabled && "opacity-50 cursor-not-allowed"
            )}
            disabled={disabled}
            id={id}
            aria-required={required}
          >
            <CalendarIcon className="mr-2 h-4 w-4" />
            {displayValue || <span className="text-muted-foreground">{placeholder}</span>}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar
            mode="single"
            selected={value ? new Date(value + 'T00:00:00') : undefined}
            onSelect={handleDateSelect}
            disabled={(date) => date < minDateObj}
            initialFocus
          />
        </PopoverContent>
      </Popover>
      <Input
        type="hidden"
        value={value}
        onChange={handleInputChange}
        disabled={disabled}
      />
    </div>
  )
}

interface DateRangeInputProps {
  startDate: string
  endDate: string
  onStartDateChange: (date: string) => void
  onEndDateChange: (date: string) => void
  placeholder?: string
  className?: string
  disabled?: boolean
}

export function DateRangeInput({
  startDate,
  endDate,
  onStartDateChange,
  onEndDateChange,
  placeholder = "Select date range",
  className,
  disabled = false
}: DateRangeInputProps) {
  const [open, setOpen] = useState(false)

  const handleStartSelect = (date: Date | undefined) => {
    if (date) {
      const formattedDate = format(date, 'yyyy-MM-dd')
      onStartDateChange(formattedDate)
      setOpen(false)
    }
  }

  const handleEndSelect = (date: Date | undefined) => {
    if (date) {
      const formattedDate = format(date, 'yyyy-MM-dd')
      onEndDateChange(formattedDate)
      setOpen(false)
    }
  }

  const startDisplay = startDate ? format(new Date(startDate + 'T00:00:00'), 'dd/MM/yyyy') : ''
  const endDisplay = endDate ? format(new Date(endDate + 'T00:00:00'), 'dd/MM/yyyy') : ''

  return (
    <div className={cn("flex flex-col space-y-2", className)}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            className={cn(
              "w-full justify-start text-left font-normal border-input bg-background",
              !startDate && !endDate && "text-muted-foreground",
              disabled && "opacity-50 cursor-not-allowed"
            )}
            disabled={disabled}
          >
            <CalendarIcon className="mr-2 h-4 w-4" />
            {(startDisplay && endDisplay) ? `${startDisplay} - ${endDisplay}` : 
             startDisplay ? `From ${startDisplay}` : 
             endDisplay ? `To ${endDisplay}` : 
             placeholder}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar
            mode="range"
            selected={{
              from: startDate ? new Date(startDate + 'T00:00:00') : undefined,
              to: endDate ? new Date(endDate + 'T00:00:00') : undefined,
            }}
            onSelect={(range) => {
              if (range?.from) {
                handleStartSelect(range.from)
              }
              if (range?.to) {
                handleEndSelect(range.to)
              }
            }}
            disabled={(date) => 
              date < new Date('1900-01-01')
            }
            numberOfMonths={2}
            initialFocus
          />
        </PopoverContent>
      </Popover>
    </div>
  )
}